import { useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CheckCircle2, XCircle, RefreshCw, Loader2, AlertTriangle, X, ShieldAlert,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { supabase } from '@/integrations/supabase/client'
import { useOrg } from '@/contexts/OrgContext'
import { invokeEdgeOrThrow } from '@/lib/edge-functions'
import { EngineStatusBanner } from './EngineStatusBanner'

// ─── Pending review row (from message_qa_reviews) ────────────────────────────
interface PendingReview {
  id: string
  schedule_id: string
  status: string
  step_type: string
  generated_subject: string | null
  generated_message: string
  regenerate_count: number
  created_at: string
}

type RegenerateHint = 'shorter' | 'more_casual' | 'different_angle'

interface HoldReviewPanelProps {
  scheduleId: string
  // Human-readable context for the header (from the schedule row).
  leadName?: string
  company?: string | null
  stepLabel?: string
  // Called after any successful decision so the parent can close the panel.
  onDecided?: () => void
  onClose?: () => void
}

// ─── HoldReviewPanel ─────────────────────────────────────────────────────────
// CA-023: makes hold_for_review schedules operable from the web app. Loads the
// pending message_qa_reviews row for a schedule, shows the generated text, and
// wires Approve / Reject / Regenerate to chief-approve-message ({review_id,
// decision, regenerate_hint?, decision_reason?}). Warns that approving does NOT
// send while the engine is OFF (the EngineStatusBanner sits next to the action).
export function HoldReviewPanel({
  scheduleId, leadName, company, stepLabel, onDecided, onClose,
}: HoldReviewPanelProps) {
  const { orgId } = useOrg()
  const queryClient = useQueryClient()
  const [rejectReason, setRejectReason] = useState('')
  const [regenHint, setRegenHint] = useState<RegenerateHint>('different_angle')
  const [showRejectBox, setShowRejectBox] = useState(false)

  // ── Load the pending review for this schedule ──────────────────────────────
  const { data: review, isLoading, error, refetch } = useQuery<PendingReview | null>({
    queryKey: ['qa-review', scheduleId, orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('message_qa_reviews')
        .select('id, schedule_id, status, step_type, generated_subject, generated_message, regenerate_count, created_at')
        .eq('schedule_id', scheduleId)
        .eq('org_id', orgId!)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (error) throw error
      return (data as PendingReview | null) ?? null
    },
    enabled: !!orgId && !!scheduleId,
  })

  // ── Invalidate both surfaces after a decision ──────────────────────────────
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['qa-review', scheduleId, orgId] })
    queryClient.invalidateQueries({ queryKey: ['qa-reviews', orgId] })
    queryClient.invalidateQueries({ queryKey: ['outreach-scheduled', orgId] })
    queryClient.invalidateQueries({ queryKey: ['outreach-history', orgId] })
  }

  const decideMutation = useMutation({
    mutationFn: async (args: {
      decision: 'approve' | 'reject' | 'regenerate'
      regenerate_hint?: RegenerateHint
      decision_reason?: string
    }) => {
      if (!review) throw new Error('No hay revisión pendiente para este envío')
      return invokeEdgeOrThrow<{ success: boolean; message?: string; decision?: string }>(
        'chief-approve-message',
        {
          review_id: review.id,
          decision: args.decision,
          orgId,
          ...(args.regenerate_hint ? { regenerate_hint: args.regenerate_hint } : {}),
          ...(args.decision_reason ? { decision_reason: args.decision_reason } : {}),
        },
        { timeoutMs: 30000 },
      )
    },
    onSuccess: (res, vars) => {
      const label =
        vars.decision === 'approve' ? 'Mensaje aprobado'
          : vars.decision === 'reject' ? 'Mensaje rechazado — paso omitido'
            : 'Regeneración solicitada'
      toast.success(res?.message ? `${label}` : label, {
        description: vars.decision === 'approve'
          ? 'No se enviará mientras el motor de outreach esté apagado.'
          : undefined,
      })
      invalidateAll()
      onDecided?.()
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Error al procesar la decisión')
    },
  })

  const busy = decideMutation.isPending

  return (
    <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium leading-tight">
            Revisión de mensaje{leadName ? ` · ${leadName}` : ''}
          </p>
          <p className="text-xs text-muted-foreground">
            {[company, stepLabel].filter(Boolean).join(' · ') || 'Mensaje en espera de aprobación'}
          </p>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Loading / error / empty states */}
      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando el mensaje generado…
        </div>
      ) : error ? (
        <div className="py-4 text-sm">
          <p className="text-red-600 dark:text-red-400 flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4" /> No se pudo cargar la revisión.
          </p>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => refetch()}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reintentar
          </Button>
        </div>
      ) : !review ? (
        <div className="py-4 text-sm text-muted-foreground flex items-center gap-1.5">
          <ShieldAlert className="h-4 w-4" />
          No hay una revisión pendiente para este envío (puede que ya se haya decidido).
        </div>
      ) : (
        <>
          {/* Generated content */}
          <div className="rounded-md border bg-background p-3 space-y-2">
            {review.generated_subject && (
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Asunto</p>
                <p className="text-sm font-medium">{review.generated_subject}</p>
              </div>
            )}
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Mensaje</p>
              <p className="text-sm whitespace-pre-wrap break-words">{review.generated_message}</p>
            </div>
            {review.regenerate_count > 0 && (
              <p className="text-[11px] text-muted-foreground">
                Regenerado {review.regenerate_count} {review.regenerate_count === 1 ? 'vez' : 'veces'}
              </p>
            )}
          </div>

          {/* Engine-state warning right next to the action (approving ≠ sending while OFF) */}
          <EngineStatusBanner compact />

          {/* Reject reason box */}
          {showRejectBox && (
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Motivo del rechazo (opcional)…"
              className="text-sm min-h-[64px]"
              disabled={busy}
            />
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              className="h-8 bg-green-600 hover:bg-green-700 text-white"
              disabled={busy}
              onClick={() => decideMutation.mutate({ decision: 'approve' })}
            >
              {busy && decideMutation.variables?.decision === 'approve'
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
              Aprobar
            </Button>

            {showRejectBox ? (
              <Button
                size="sm"
                variant="destructive"
                className="h-8"
                disabled={busy}
                onClick={() => decideMutation.mutate({ decision: 'reject', decision_reason: rejectReason.trim() || undefined })}
              >
                {busy && decideMutation.variables?.decision === 'reject'
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : <XCircle className="h-3.5 w-3.5 mr-1" />}
                Confirmar rechazo
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800"
                disabled={busy}
                onClick={() => setShowRejectBox(true)}
              >
                <XCircle className="h-3.5 w-3.5 mr-1" />
                Rechazar
              </Button>
            )}

            {/* Regenerate with hint */}
            <div className="flex items-center gap-1.5 ml-auto">
              <select
                value={regenHint}
                onChange={(e) => setRegenHint(e.target.value as RegenerateHint)}
                disabled={busy}
                className="h-8 rounded-md border border-input bg-transparent px-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
              >
                <option value="different_angle">Otro ángulo</option>
                <option value="shorter">Más corto</option>
                <option value="more_casual">Más casual</option>
              </select>
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                disabled={busy}
                onClick={() => decideMutation.mutate({ decision: 'regenerate', regenerate_hint: regenHint })}
              >
                {busy && decideMutation.variables?.decision === 'regenerate'
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                Regenerar
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
